import React, { useEffect, useRef, useState, useCallback } from "react"
import Webcam from "react-webcam"
import axios from "../lib/axios"
import { getDistance } from "geolib"
import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { API_BASE_URL } from "../config"
import { useLoader } from "../contexts/LoaderContext";
import { useJourney } from "@/contexts/JourneyContext";
import { Loader2, Camera, MapPin, RefreshCw, CheckCircle2, AlertCircle } from "lucide-react";

// ----------------------------------------------------------------------
// TYPES
// ----------------------------------------------------------------------

interface BackendCoordinates {
  latitude: number;
  longitude: number;
}

interface AddressDetails {
  address_line1: string;
  address_line2: string;
  address_line3: string;
  landmark: string;
  pincode: string;
}

interface ValidationThresholds {
  THRESHOLD_METERS: number;
  THRESHOLD_ACCURACY: number;
  THRESHOLD_STALE_DATA_MILLISEC: number;
}

interface GeoPosition {
  lat: number;
  lon: number;
  accuracy: number;
  timestamp: number;
}

type Capture = {
  videoBlob: Blob
  videoUrl: string
  lat: number
  lon: number
  distanceMeters: number
  timestamp: number
  accuracy: number
}

// ----------------------------------------------------------------------
// CONSTANTS
// ----------------------------------------------------------------------

const UPLOAD_TIMEOUT_MS = 60000
const RECORDING_DURATION_MS = 10_000

const VIDEO_CONSTRAINTS: MediaTrackConstraints = {
  facingMode: "environment",
  width: { ideal: 1920 },
  height: { ideal: 1080 },
};

// ----------------------------------------------------------------------
// SUB-COMPONENTS
// ----------------------------------------------------------------------

const FullPageLoader = ({ message = "Loading..." }: { message?: string }) => (
  <div className="fixed inset-0 bg-black/90 z-50 flex flex-col items-center justify-center text-white p-4">
    <Loader2 className="w-12 h-12 animate-spin mb-4 text-primary" />
    <p className="text-lg font-medium">{message}</p>
  </div>
);

const InstructionsScreen = ({ address, thresholds, onProceed, isRequesting }: { address: AddressDetails, thresholds: ValidationThresholds, onProceed: () => void, isRequesting: boolean }) => (
  <div className="min-h-screen bg-black text-white flex flex-col p-6 animate-in fade-in duration-300">
    <div className="flex-1 flex flex-col items-center justify-center text-center max-w-lg mx-auto space-y-6">
      <div className="text-2xl font-semibold mb-1">INSTRUCTIONS</div>

      <div className="mb-2 rounded-lg bg-gray-100 p-3 text-center text-xs text-gray-600 font-bold w-full">
        By proceeding, you consent to the use of your camera, audio, video, and location for identity and location verification only.
      </div>

      <div className="text-sm">
        Please record the surrounding, board and interior of the shop/ establishment at the following address:
      </div>

      <div className="bg-white/10 rounded-xl p-4 w-full border border-white/10 backdrop-blur-sm text-left">
        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">ADDRESS:</h3>
        <div className="space-y-1 text-sm font-medium">
          <p>{address.address_line1}</p>
          <p>{address.address_line2}</p>
          {address.address_line3 && <p>{address.address_line3}</p>}
          <p>{address.landmark}</p>
          <p className="text-primary pt-1">{address.pincode}</p>
        </div>
      </div>

      <div className="space-y-3 text-sm text-gray-300 text-left w-full">
        <div className="flex items-center gap-3 bg-white/5 p-3 rounded-lg">
          <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0" />
          <span>Ensure the shop board and interior are clearly visible.</span>
        </div>
        <div className="flex items-center gap-3 bg-white/5 p-3 rounded-lg">
          <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0" />
          <span>Video recording will automatically stop after 10 seconds.</span>
        </div>
        <div className="flex items-center gap-3 bg-white/5 p-3 rounded-lg">
          <MapPin className="w-5 h-5 text-blue-500 shrink-0" />
          <span>Record within {thresholds.THRESHOLD_METERS} meters of the above address.</span>
        </div>
      </div>

    </div>

    <div className="pt-6 w-full max-w-lg mx-auto">
      <Button size="lg" className="w-full text-lg font-semibold h-12" onClick={onProceed} disabled={isRequesting}>
        {isRequesting ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Requesting Access...
          </>
        ) : (
          "Proceed"
        )}
      </Button>
      {isRequesting && (
        <p className="text-xs text-gray-400 text-center mt-3">
          This can take up to 2 minutes
        </p>
      )}
    </div>
  </div>
);

const PermissionRequestScreen = ({
  type,
  onGrant,
  onInstruction
}: {
  type: "camera" | "location",
  onGrant: () => void,
  onInstruction: () => void
}) => {
  const isCam = type === "camera";
  const Icon = isCam ? Camera : MapPin;
  const title = isCam ? "Camera & Mic Permission Required" : "Location Required";

  const desc = isCam
    ? "We need camera and microphone access to record the shop video."
    : "Location access is mandatory to verify this video.\nPlease enable location permissions in your browser or device settings.";

  return (
    <div className="fixed inset-0 bg-black/95 z-40 flex flex-col items-center justify-center p-6 text-center text-white">
      <div className="w-20 h-20 bg-gray-800 rounded-full flex items-center justify-center mb-6 animate-pulse">
        <Icon className="w-10 h-10 text-white" />
      </div>
      <h2 className="text-xl font-semibold mb-3">{title}</h2>

      {!isCam && (
        <div className="text-xs text-gray-400 mb-6 font-mono">
          Settings → Site Settings → Location → Allow
        </div>
      )}

      <p className="text-gray-300 mb-8 max-w-xs mx-auto text-sm leading-relaxed whitespace-pre-line">{desc}</p>

      <div className="flex flex-col gap-3 w-full max-w-xs">
        <Button size="lg" onClick={onGrant} className="w-full bg-white text-black hover:bg-gray-200">
          {isCam ? "Allow Access" : "Allow Location"}
        </Button>
        <Button variant="outline" onClick={onInstruction} className="w-full bg-white text-black hover:bg-gray-200">
          Instructions For Apple Devices
        </Button>

        {!isCam && (
          <Button
            className="w-full mt-2"
            onClick={() => window.location.reload()}
          >
            Retry After Enabling Location
          </Button>
        )}
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------
// MAIN PAGE
// ----------------------------------------------------------------------

export default function ShopVideoLocVerification() {
  const navigate = useNavigate();
  const webcamRef = useRef<Webcam | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const mediaStreamRef = useRef<MediaStream | null>(null);

  // -- Global Loader --
  const { setLoading: setGlobalLoading } = useLoader();
  const { refresh: refreshJourney } = useJourney();

  // -- State --
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [backendData, setBackendData] = useState<{
    address: AddressDetails;
    thresholds: ValidationThresholds;
    coord: BackendCoordinates;
  } | null>(null);

  const [step, setStep] = useState<"instructions" | "permissions" | "capture" | "preview">("instructions");

  // Permissions State
  const [permissionStatus, setPermissionStatus] = useState<{
    camera: "unknown" | "granted" | "denied";
    location: "unknown" | "granted" | "denied";
  }>({ camera: "unknown", location: "unknown" });

  const [isRequestingPermissions, setIsRequestingPermissions] = useState(false);

  // Capture State
  const [position, setPosition] = useState<GeoPosition | null>(null);
  const [capture, setCapture] = useState<Capture | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);

  // -- 1. Load Data on Mount --
  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      try {
        setLoadingInitial(true);
        const res = await axios.post(`${API_BASE_URL}/api/get_address_for_taking_shop_video/`);
        if (!mounted) return;

        setBackendData({
          address: {
            address_line1: res.data.address_line1,
            address_line2: res.data.address_line2,
            address_line3: res.data.address_line3,
            landmark: res.data.landmark,
            pincode: res.data.pincode,
          },
          thresholds: {
            THRESHOLD_METERS: res.data.THRESHOLD_METERS ?? 100,
            THRESHOLD_ACCURACY: res.data.THRESHOLD_ACCURACY ?? 100,
            THRESHOLD_STALE_DATA_MILLISEC: res.data.THRESHOLD_STALE_DATA_MILLISEC ?? 30000,
          },
          coord: {
            latitude: Number(res.data.latitude),
            longitude: Number(res.data.longitude),
          },
        });
      } catch (err) {
        console.error(err);
        alert("Failed to load verification configuration.");
      } finally {
        if (mounted) setLoadingInitial(false);
      }
    };
    fetchData();
    return () => { mounted = false; };
  }, []);

  // -- 2. Distance Calculation --
  const computeDistance = (lat: number, lon: number) => {
    if (!backendData?.coord) return -1;
    return Math.round(getDistance({ latitude: lat, longitude: lon }, backendData.coord));
  };

  // -- 3. Location Watcher --
  const watchIdRef = useRef<number | null>(null);

  const startWatching = useCallback(() => {
    if (!navigator.geolocation || !backendData) return;
    if (watchIdRef.current !== null) navigator.geolocation.clearWatch(watchIdRef.current);

    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        setPermissionStatus(prev => ({ ...prev, location: "granted" }));
        const { THRESHOLD_STALE_DATA_MILLISEC, THRESHOLD_ACCURACY } = backendData.thresholds;

        if (Date.now() - pos.timestamp > THRESHOLD_STALE_DATA_MILLISEC) {
          setGeoError("Stale GPS data.");
          return;
        }
        if (pos.coords.accuracy > THRESHOLD_ACCURACY) {
          setGeoError(`Low GPS accuracy: ${Math.round(pos.coords.accuracy)}m`);
          return;
        }

        setGeoError(null);
        setPosition({
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          timestamp: pos.timestamp,
        });
      },
      (err) => {
        console.error("Geo error:", err);
        setGeoError("Location access unavailable.");
        if (err.code === 1) setPermissionStatus(prev => ({ ...prev, location: "denied" }));
      },
      { enableHighAccuracy: true, maximumAge: 60000, timeout: 30000 }
    );
  }, [backendData]);

  // -- 4. Permission Handlers --
  const requestPermissions = async () => {
    setIsRequestingPermissions(true);
    setStep("permissions");

    let locGranted = false;
    let camGranted = false;

    // 1. Location
    try {
      await new Promise<void>((resolve, reject) => {
        if (!navigator.geolocation) return reject();
        navigator.geolocation.getCurrentPosition(
          () => {
            setPermissionStatus(p => ({ ...p, location: "granted" }));
            startWatching();
            locGranted = true;
            resolve();
          },
          (err) => {
            console.warn("Location denied", err);
            setPermissionStatus(p => ({ ...p, location: "denied" }));
            reject();
          }
        );
      });
    } catch { }

    // 2. Camera & Mic
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      // We don't stop tracks here typically for video rec, but Webcam component requests its own stream
      // However, to just Check permissions, we stop them immediately.
      stream.getTracks().forEach(t => t.stop());
      setPermissionStatus(p => ({ ...p, camera: "granted" }));
      camGranted = true;
    } catch (err) {
      console.warn("Camera/Mic denied", err);
      setPermissionStatus(p => ({ ...p, camera: "denied" }));
    }

    setIsRequestingPermissions(false);

    if (locGranted && camGranted) {
      setStep("capture");
    }
  };

  useEffect(() => {
    if (step === "permissions") {
      if (permissionStatus.camera === "granted" && permissionStatus.location === "granted") {
        setStep("capture");
      }
    }
  }, [permissionStatus, step]);


  const showIOSHelp = (type: "location" | "camera") => {
    if (type === 'location') {
      const userConfirmed = confirm(
        "To enable location access in iOS:\n\n" +
        "1. Go to Settings -> Privacy & Security -> Location Services\n" +
        "2. Activate Location Services\n" +
        "3. Safari Websites -> While Using the App\n\n" +
        "After doing this you may need to close the tab to reflect changes Press OK and close the tab, or Cancel to reload the page."
      );
      if (!userConfirmed) window.location.reload();
    } else {
      alert(
        "To enable camera/microphone access on iPhone:\n\n" +
        "1. Go to Settings → Privacy & Security → Camera (and Microphone)\n" +
        "2. Enable access for Safari / your browser\n" +
        "3. Reload this page after enabling"
      );
    }
  };

  // -- 5. Recording Logic --
  const startRecording = () => {
    if (!position || !mediaStreamRef.current) return;
    if (!window.MediaRecorder || !MediaRecorder.isTypeSupported("video/webm")) {
      setGeoError("Video recording not supported.");
      return;
    }

    recordedChunksRef.current = [];
    const recorder = new MediaRecorder(mediaStreamRef.current, { mimeType: "video/webm" });

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) recordedChunksRef.current.push(e.data);
    };


    recorder.onstop = () => {
      const blob = new Blob(recordedChunksRef.current, { type: "video/webm" });
      const url = URL.createObjectURL(blob);

      setCapture({
        videoBlob: blob,
        videoUrl: url,
        lat: position.lat,
        lon: position.lon,
        distanceMeters: computeDistance(position.lat, position.lon),
        timestamp: Date.now(),
        accuracy: position.accuracy,
      });
      setStep("preview");
    };

    recorder.start();
    mediaRecorderRef.current = recorder;
    setIsRecording(true);


    window.setTimeout(() => {
      if (recorder.state === "recording") {
        recorder.stop();
        setIsRecording(false);
      }
    }, RECORDING_DURATION_MS);
  };

  // -- 6. Submit Logic --
  const handleSubmit = async () => {
    if (!capture) return;
    setIsSubmitting(true);
    setGlobalLoading(true);

    const form = new FormData();
    form.append("file", capture.videoBlob, `shop-${capture.timestamp}.webm`);
    form.append("latitude", String(capture.lat));
    form.append("longitude", String(capture.lon));
    form.append("distance_meters", String(capture.distanceMeters));
    form.append("accuracy", String(capture.accuracy));


    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS);


      const res = await axios.post(
        `${API_BASE_URL}/api/set_shop_video_geolocation_pushload/`,
        form,
        { signal: controller.signal }
      );

      clearTimeout(timeoutId);

      refreshJourney().then(() => {
        setTimeout(() => {
          if (res.data.next_page_url) {
            navigate(res.data.next_page_url);
          } else {
            navigate("/final-offer");
          }
        }, 150);
      }).catch(() => {
        setTimeout(() => {
          if (res.data.next_page_url) {
            navigate(res.data.next_page_url);
          } else {
            navigate("/final-offer");
          }
        }, 150);
      });
    } catch (err) {
      console.error(err);
      alert("Upload failed. Please retry.");
      // setCapture(null); // Optional: keep capture in preview to retry?
    } finally {
      setIsSubmitting(false);
      setGlobalLoading(false);
    }
  };


  // -- RENDER --

  if (loadingInitial || !backendData) return <FullPageLoader message="Fetching verification details..." />;

  // 1. Instructions
  if (step === "instructions") {
    return (
      <InstructionsScreen
        address={backendData.address}
        thresholds={backendData.thresholds}
        onProceed={requestPermissions}
        isRequesting={isRequestingPermissions}
      />
    );
  }

  // 2. Permissions
  if (step === "permissions") {
    if (permissionStatus.location !== "granted") {
      return (
        <PermissionRequestScreen
          type="location"
          onGrant={() => {
            navigator.geolocation.getCurrentPosition(
              () => {
                setPermissionStatus(p => ({ ...p, location: "granted" }));
                startWatching();
              },
              (e) => {
                console.warn(e);
                setPermissionStatus(p => ({ ...p, location: "denied" }));
              },
              { enableHighAccuracy: true }
            );
          }}
          onInstruction={() => showIOSHelp("location")}
        />
      );
    }
    if (permissionStatus.camera !== "granted") {
      return (
        <PermissionRequestScreen
          type="camera"
          onGrant={async () => {
            try {
              const s = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
              s.getTracks().forEach(t => t.stop());
              setPermissionStatus(p => ({ ...p, camera: "granted" }));
            } catch {
              setPermissionStatus(p => ({ ...p, camera: "denied" }));
            }
          }}
          onInstruction={() => showIOSHelp("camera")}
        />
      );
    }
  }

  // 3. Capture & Preview
  return (
    <div className="fixed inset-0 bg-black flex flex-col">
      {/* HEADER */}
      <div className="absolute top-0 inset-x-0 z-20 bg-gradient-to-b from-black/80 to-transparent p-4 pt-6">
        <h2 className="text-white text-center font-bold text-lg drop-shadow-md leading-tight">
          {step === "preview" ? "Verify Video" : "Record Shop Video"}
        </h2>
      </div>

      <div className="relative flex-1 bg-gray-900 overflow-hidden">
        {step === "capture" ? (
          <>
            <Webcam
              ref={webcamRef}
              audio={true}
              muted={true} // Mute local playback to avoid feedback
              videoConstraints={VIDEO_CONSTRAINTS}
              onUserMedia={(stream) => {
                mediaStreamRef.current = stream;
              }}
              onUserMediaError={() => setPermissionStatus(p => ({ ...p, camera: "denied" }))}
              className="absolute inset-0 w-full h-full object-cover"
            />

            {/* Live Stats */}
            <div className="absolute bottom-6 inset-x-6 flex flex-col gap-4 z-30">
              {position && (
                <div className="text-center">
                  <p className="text-white text-sm font-medium drop-shadow-md">
                    Distance: {computeDistance(position.lat, position.lon)} m | Accuracy: {position.accuracy.toFixed(2)}
                  </p>
                  {backendData.thresholds && computeDistance(position.lat, position.lon) > backendData.thresholds.THRESHOLD_METERS && (
                    <p className="text-red-400 text-xs font-semibold max-w-xs mx-auto text-center break-words bg-black/40 p-1 rounded mt-1">
                      Move closer to the address for better verification.
                    </p>
                  )}
                </div>
              )}

              {geoError && <p className="text-red-500 text-sm font-bold text-center bg-black/50 p-2 rounded">{geoError}</p>}

              <div className="flex justify-center">
                <Button
                  onClick={startRecording}
                  disabled={!position || isRecording || !!geoError}
                  className={`w-20 h-20 rounded-full border-4 shadow-lg active:scale-95 transition-all flex items-center justify-center p-0 ${isRecording ? "bg-red-600 border-red-200" : "bg-white border-gray-300"}`}
                >
                  {isRecording ? (
                    <div className="w-8 h-8 bg-white rounded-sm" />
                  ) : (
                    <div className="w-16 h-16 rounded-full border-2 border-black" />
                  )}
                </Button>
              </div>
              <p className="text-white text-xs text-center">{isRecording ? "Recording... (Max 10s)" : "Tap to Start Recording"}</p>
            </div>
          </>
        ) : (
          capture && (
            <div className="relative w-full h-full flex flex-col">
              <video
                src={capture.videoUrl}
                controls
                className="flex-1 object-contain bg-black w-full"
              />

              {/* Validation Status Overlay */}
              <div className="absolute bottom-0 inset-x-0 bg-black/80 backdrop-blur-xl p-6 rounded-t-2xl z-40 border-t border-white/10">
                <div className="flex flex-col items-center text-center gap-2 mb-6">
                  {capture.distanceMeters <= backendData.thresholds.THRESHOLD_METERS ? (
                    <div className="w-12 h-12 rounded-full bg-green-500/20 text-green-500 flex items-center justify-center mb-1">
                      <CheckCircle2 className="w-6 h-6" />
                    </div>
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-red-500/20 text-red-500 flex items-center justify-center mb-1">
                      <AlertCircle className="w-6 h-6" />
                    </div>
                  )}

                  <h3 className="text-white text-lg font-semibold">
                    {capture.distanceMeters <= backendData.thresholds.THRESHOLD_METERS
                      ? "Location Verified"
                      : "Too Far from Shop"}
                  </h3>
                  <p className="text-gray-400 text-sm">
                    Recorded {capture.distanceMeters}m from registered address.
                    {capture.distanceMeters > backendData.thresholds.THRESHOLD_METERS && ` (Limit: ${backendData.thresholds.THRESHOLD_METERS}m)`}
                  </p>
                </div>

                <div className="flex gap-4">
                  <Button variant="secondary" className="flex-1 h-12" onClick={() => {
                    setCapture(null);
                    setStep("capture");
                  }}>
                    <RefreshCw className="mr-2 w-4 h-4" /> Record Again
                  </Button>
                  <Button
                    className={`flex-1 h-12 ${capture.distanceMeters > backendData.thresholds.THRESHOLD_METERS ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"}`}
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Uploading..." : "Submit Video"}
                  </Button>
                </div>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
}
